import { PayerAdjustmentGroupType } from '../../api/server/models/PayerAdjustmentGroupType';
import { normalizePayerAdjustmentGroup } from '../dictionary/payer-adjustment-groups';

/**
 * The code tables the Remits page reads (FE-163, crosswalk §7). Client-side by design: none of these come from the
 * API, and every one is fixed by the X12 835 5010 implementation guide (005010X221A1).
 */

/** CAS01 as the 835 writes it. */
const X12_GROUP: Record<string, string> = {
    [PayerAdjustmentGroupType.CONTRACTUALOBLIGATIONS]: 'CO',
    [PayerAdjustmentGroupType.OTHERADJUSTMENTS]: 'OA',
    [PayerAdjustmentGroupType.PAYERINITIATEDREDUCTIONS]: 'PI',
    [PayerAdjustmentGroupType.PATIENTRESPONSIBILITY]: 'PR',
    [PayerAdjustmentGroupType.CORRECTIONSANDREVERSALS]: 'CR',
};

/**
 * `CO` from `CONTRACTUAL_OBLIGATIONS` — both remit sources send the long name (measured 2026-10-08, ERA and EOB lines
 * alike). The X12 code, not the dictionary's display code: that one writes payer-initiated reductions as `PIR`, which
 * no remit prints and which the allowed amount, summed over CO, OA and PI, would then silently miss. A group the
 * table does not know is kept as given.
 */
export const shortGroup = (group: string | undefined): string =>
    group ? X12_GROUP[normalizePayerAdjustmentGroup(group)] || group : '';

/**
 * Claim status, from the API's enum name to CLP02 and the words a biller reads.
 *
 * The crosswalk says `statusCode` is CLP02; on production it is the ENUM NAME (`PROCESSED_AS_PRIMARY`,
 * `REVERSAL_OF_PREVIOUS_PAYMENT` — measured 2026-10-08), so the number on screen is mapped back from it. Statuses
 * an 835 cannot carry (the 277 ones — pended, suspended…) have no CLP02 and show their words only.
 */
const CLAIM_STATUS: Record<string, { code: string; label: string }> = {
    PROCESSED_AS_PRIMARY: { code: '1', label: 'Processed as primary' },
    PROCESSED_AS_SECONDARY: { code: '2', label: 'Processed as secondary' },
    PROCESSED_AS_TERTIARY: { code: '3', label: 'Processed as tertiary' },
    DENIED: { code: '4', label: 'Denied' },
    PROCESSED_AS_PRIMARY_FORWARDED_TO_ADDITIONAL_PAYERS: { code: '19', label: 'Processed as primary, forwarded to additional payer(s)' },
    PROCESSED_AS_SECONDARY_FORWARDED_TO_ADDITIONAL_PAYERS: { code: '20', label: 'Processed as secondary, forwarded to additional payer(s)' },
    PROCESSED_AS_TERTIARY_FORWARDED_TO_ADDITIONAL_PAYERS: { code: '21', label: 'Processed as tertiary, forwarded to additional payer(s)' },
    REVERSAL_OF_PREVIOUS_PAYMENT: { code: '22', label: 'Reversal of previous payment' },
    NOT_OUR_CLAIM_FORWARDED_TO_ADDITIONAL_PAYERS: { code: '23', label: 'Not our claim, forwarded to additional payer(s)' },
    PREDETERMINATION_PRICING_ONLY_NO_PAYMENT: { code: '25', label: 'Predetermination pricing only, no payment' },
};

/** `PROCESSED_AS_PRIMARY` → `{ code: '1', label: 'Processed as primary' }`; an unmapped name gets readable words. */
export const claimStatus = (status: string | undefined): { code?: string; label: string } => {
    if (!status) {
        return { label: '' };
    }
    const known = CLAIM_STATUS[status] || Object.values(CLAIM_STATUS).find(entry => entry.code === status);
    if (known) {
        return known;
    }
    const words = status.toLowerCase().replace(/_/g, ' ');
    return { label: words.charAt(0).toUpperCase() + words.slice(1) };
};

/**
 * PLB03-1 provider adjustment reason codes, with the API's EOB enum name for each — the two lists are the same
 * set, in the same order (`EOBProviderAdjustmentCodeType`).
 */
export const PROVIDER_ADJUSTMENT_CODES: Array<{ code: string; type: string; label: string }> = [
    { code: '50', type: 'LATE_CHARGE', label: 'Late charge' },
    { code: '51', type: 'INTEREST_PENALTY_CHARGE', label: 'Interest penalty charge' },
    { code: '72', type: 'AUTHORIZED_RETURN', label: 'Authorized return' },
    { code: '90', type: 'EARLY_PAYMENT_ALLOWANCE', label: 'Early payment allowance' },
    { code: 'AH', type: 'ORIGINATION_FEE', label: 'Origination fee' },
    { code: 'AM', type: 'APPLIED_TO_BORROWERS_ACCOUNT', label: "Applied to borrower's account" },
    { code: 'AP', type: 'ACCELERATION_OF_BENEFITS', label: 'Acceleration of benefits' },
    { code: 'B2', type: 'REBATE', label: 'Rebate' },
    { code: 'B3', type: 'RECOVERY_ALLOWANCE', label: 'Recovery allowance' },
    { code: 'BD', type: 'BAD_DEBT_ADJUSTMENT', label: 'Bad debt adjustment' },
    { code: 'BN', type: 'BONUS', label: 'Bonus' },
    { code: 'C5', type: 'TEMPORARY_ALLOWANCE', label: 'Temporary allowance' },
    { code: 'CR', type: 'CAPITATION_INTEREST', label: 'Capitation interest' },
    { code: 'CS', type: 'ADJUSTMENT', label: 'Adjustment' },
    { code: 'CT', type: 'CAPITATION_PAYMENT', label: 'Capitation payment' },
    { code: 'CV', type: 'CAPITAL_PASSTHRU', label: 'Capital passthru' },
    { code: 'CW', type: 'CERTIFIED_REGISTERED_NURSE_ANESTHETIST_PASSTHRU', label: 'Certified registered nurse anesthetist passthru' },
    { code: 'DM', type: 'DIRECT_MEDICAL_EDUCATION_PASSTHRU', label: 'Direct medical education passthru' },
    { code: 'E3', type: 'WITHHOLDING', label: 'Withholding' },
    { code: 'FB', type: 'FORWARDING_BALANCE', label: 'Forwarding balance' },
    { code: 'FC', type: 'FUND_ALLOCATION', label: 'Fund allocation' },
    { code: 'GO', type: 'GRADUATION_MEDICAL_EDUCATION_PASSTHRU', label: 'Graduate medical education passthru' },
    { code: 'HM', type: 'HEMOPHILIA_CLOTTING_FACTOR_SUPPLEMENT', label: 'Hemophilia clotting factor supplement' },
    { code: 'IP', type: 'INCENTIVE_PREMIUM_PAYMENT', label: 'Incentive premium payment' },
    { code: 'IR', type: 'INTERNAL_REVENUE_SERVICE_WITHHOLDING', label: 'Internal Revenue Service withholding' },
    { code: 'IS', type: 'INTERIM_SETTLEMENT', label: 'Interim settlement' },
    { code: 'J1', type: 'NONREIMBURSABLE', label: 'Nonreimbursable' },
    { code: 'L3', type: 'PENALTY', label: 'Penalty' },
    { code: 'L6', type: 'INTEREST_OWED', label: 'Interest owed' },
    { code: 'LE', type: 'LEVY', label: 'Levy' },
    { code: 'LS', type: 'LUMP_SUM', label: 'Lump sum' },
    { code: 'OA', type: 'ORGAN_ACQUISITION_PASSTHRU', label: 'Organ acquisition passthru' },
    { code: 'OB', type: 'OFFSET_FOR_AFFILIATED_PROVIDERS', label: 'Offset for affiliated providers' },
    { code: 'PI', type: 'PERIODIC_INTERIM_PAYMENT', label: 'Periodic interim payment' },
    { code: 'PL', type: 'PAYMENT_FINAL', label: 'Payment final' },
    { code: 'RA', type: 'RETROACTIVITY_ADJUSTMENT', label: 'Retro-activity adjustment' },
    { code: 'RE', type: 'RETURN_ON_EQUITY', label: 'Return on equity' },
    { code: 'SL', type: 'STUDENT_LOAN_REPAYMENT', label: 'Student loan repayment' },
    { code: 'TL', type: 'THIRD_PARTY_LIABILITY', label: 'Third party liability' },
    { code: 'WO', type: 'OVERPAYMENT_RECOVERY', label: 'Overpayment recovery' },
    { code: 'WU', type: 'UNSPECIFIED_RECOVERY', label: 'Unspecified recovery' },
];

const BY_CODE = new Map(PROVIDER_ADJUSTMENT_CODES.map(entry => [entry.code, entry]));
const BY_TYPE = new Map(PROVIDER_ADJUSTMENT_CODES.map(entry => [entry.type, entry]));

/** A PLB03-1 code's words; an unknown code is shown as itself rather than guessed at. */
export const providerAdjustmentLabel = (code: string): string => BY_CODE.get(code)?.label || code;

/** The X12 code for an EOB's provider adjustment type: `RETROACTIVITY_ADJUSTMENT` → `RA`. */
export const providerAdjustmentCode = (type: string | undefined): string => (type ? BY_TYPE.get(type)?.code || type : '');
